import { useEffect, useState, useCallback } from "react";
import { Plus, Trash, PencilSimple, Bank, Money, CreditCard, ListDashes, ArrowDownLeft, ArrowUpRight, ArrowsLeftRight, FileXls, FilePdf, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney, formatDate } from "@/lib/format";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = { name: "", bank_name: "", type: "bank", currency: "TRY", balance: "", iban: "" };
const INCOME_CATS = ["Satış", "Hizmet", "Faiz", "Tahsilat", "Diğer Gelir"];
const EXPENSE_CATS = ["Maaş", "Kira", "Tedarik", "Elektrik/Su", "Pazarlama", "Vergi", "Lojistik", "Ödeme", "Diğer"];
const catsFor = (type, current) => {
  const base = type === "income" ? INCOME_CATS : EXPENSE_CATS;
  return current && !base.includes(current) ? [current, ...base] : base;
};
const TYPE_META = {
  bank: { label: "Banka", icon: Bank },
  cash: { label: "Nakit / Kasa", icon: Money },
  credit: { label: "Kredi Kartı", icon: CreditCard },
};

export default function Accounts() {
  const { currencies } = useCurrency();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get("/accounts").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...a, balance: String(a.balance) }); setEditId(a.id); setOpen(true); };

  const submit = async () => {
    if (!form.name) return toast.error("Hesap adı girin");
    const payload = { ...form, balance: Number(form.balance) || 0 };
    try {
      if (editId) await api.put(`/accounts/${editId}`, payload);
      else await api.post("/accounts", payload);
      toast.success(editId ? "Hesap güncellendi" : "Hesap eklendi");
      setOpen(false);
      load();
    } catch (e) { toast.error("Kaydedilemedi"); }
  };

  const remove = async (id) => {
    await api.delete(`/accounts/${id}`);
    toast.success("Hesap silindi");
    load();
  };

  const [moveOpen, setMoveOpen] = useState(false);
  const [movements, setMovements] = useState({ account: null, transactions: [] });
  const [moveFilter, setMoveFilter] = useState({ start: "", end: "", type: "all" });
  const [activeAcc, setActiveAcc] = useState(null);

  const fetchMovements = useCallback(async (accId, filter) => {
    const params = new URLSearchParams();
    if (filter.start) params.set("start", filter.start);
    if (filter.end) params.set("end", filter.end);
    if (filter.type && filter.type !== "all") params.set("type", filter.type);
    const qs = params.toString();
    try {
      const r = await api.get(`/accounts/${accId}/transactions${qs ? `?${qs}` : ""}`);
      setMovements(r.data);
    } catch (e) { toast.error("Hareketler yüklenemedi"); }
  }, []);

  const openMovements = async (a) => {
    const f = { start: "", end: "", type: "all" };
    setActiveAcc(a);
    setMoveFilter(f);
    setMovements({ account: a, transactions: [] });
    setMoveOpen(true);
    fetchMovements(a.id, f);
  };

  const applyFilter = (patch) => {
    const f = { ...moveFilter, ...patch };
    setMoveFilter(f);
    if (activeAcc) fetchMovements(activeAcc.id, f);
  };

  const [txEdit, setTxEdit] = useState(null); // {id, amount, description, date, type, category}
  const openTxEdit = (t) => setTxEdit({ id: t.id, amount: String(t.amount), description: t.description || "", date: (t.date || "").slice(0, 10), type: t.type, category: t.category || "" });
  const setTxType = (v) => setTxEdit((f) => {
    const cats = v === "income" ? INCOME_CATS : EXPENSE_CATS;
    return { ...f, type: v, category: cats.includes(f.category) ? f.category : cats[0] };
  });
  const saveTxEdit = async () => {
    if (!txEdit.amount || Number(txEdit.amount) <= 0) return toast.error("Geçerli tutar girin");
    try {
      await api.put(`/transactions/${txEdit.id}`, {
        amount: Number(txEdit.amount), description: txEdit.description,
        date: txEdit.date ? new Date(txEdit.date).toISOString() : null,
        type: txEdit.type, category: txEdit.category,
      });
      toast.success("Hareket güncellendi");
      setTxEdit(null);
      if (activeAcc) fetchMovements(activeAcc.id, moveFilter);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Güncellenemedi"); }
  };
  const removeTx = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Hareket silindi");
      if (activeAcc) fetchMovements(activeAcc.id, moveFilter);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi"); }
  };
  const isEditable = (t) => !t.transfer_group && !t.payment_id;

  const exportStatement = (fmt) => {
    if (!activeAcc) return;
    const params = new URLSearchParams({ format: fmt });
    if (moveFilter.start) params.set("start", moveFilter.start);
    if (moveFilter.end) params.set("end", moveFilter.end);
    if (moveFilter.type && moveFilter.type !== "all") params.set("type", moveFilter.type);
    const ext = fmt === "pdf" ? "pdf" : "xlsx";
    downloadFile(`/accounts/${activeAcc.id}/statement?${params.toString()}`, `ekstre-${activeAcc.name}.${ext}`)
      .catch(() => toast.error("Dışa aktarılamadı"));
  };

  const [xferOpen, setXferOpen] = useState(false);
  const [xfer, setXfer] = useState({ from_account_id: "", to_account_id: "", amount: "", to_amount: "", description: "" });
  const setX = (k, v) => setXfer((f) => ({ ...f, [k]: v }));
  const fromAcc = items.find((a) => a.id === xfer.from_account_id);
  const toAcc = items.find((a) => a.id === xfer.to_account_id);
  const diffCurrency = fromAcc && toAcc && fromAcc.currency !== toAcc.currency;
  const openXfer = () => { setXfer({ from_account_id: "", to_account_id: "", amount: "", to_amount: "", description: "" }); setXferOpen(true); };
  const submitXfer = async () => {
    if (!xfer.from_account_id || !xfer.to_account_id) return toast.error("Kaynak ve hedef hesap seçin");
    if (xfer.from_account_id === xfer.to_account_id) return toast.error("Hesaplar farklı olmalı");
    if (!xfer.amount || Number(xfer.amount) <= 0) return toast.error("Geçerli tutar girin");
    try {
      await api.post("/accounts/transfer", {
        from_account_id: xfer.from_account_id, to_account_id: xfer.to_account_id,
        amount: Number(xfer.amount), to_amount: diffCurrency && xfer.to_amount ? Number(xfer.to_amount) : null,
        description: xfer.description,
      });
      toast.success("Transfer tamamlandı");
      setXferOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transfer başarısız"); }
  };

  const [histOpen, setHistOpen] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const openHistory = async () => {
    setHistOpen(true);
    try {
      const r = await api.get("/transfers");
      setTransfers(r.data);
    } catch (e) { toast.error("Transfer geçmişi yüklenemedi"); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light tracking-tight">Banka Hesapları</h1>
          <p className="text-sm text-muted-foreground mt-1">Banka ve nakit hesaplarınızı yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openHistory} data-testid="transfer-history-btn" variant="outline" className="border-border hover:bg-secondary">
            <ClockCounterClockwise size={18} className="mr-1" /> Transfer Geçmişi
          </Button>
          <Button onClick={openXfer} data-testid="transfer-btn" variant="outline" className="border-border hover:bg-secondary">
            <ArrowsLeftRight size={18} className="mr-1" /> Transfer
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-account-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus size={18} className="mr-1" /> Yeni Hesap
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-heading">{editId ? "Hesabı Düzenle" : "Yeni Hesap"}</DialogTitle>
              <DialogDescription className="text-muted-foreground">Banka veya nakit hesabınızın bilgilerini girin.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Hesap Adı</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="acc-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Banka</Label>
                  <Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="acc-bank" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tür</Label>
                  <Select value={form.type} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="acc-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Bakiye</Label>
                  <Input type="number" value={form.balance} onChange={(e) => set("balance", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="acc-balance" placeholder="0" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Para Birimi</Label>
                  <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="acc-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">IBAN (opsiyonel)</Label>
                <Input value={form.iban} onChange={(e) => set("iban", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="acc-iban" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} data-testid="acc-save" className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <p className="text-sm text-muted-foreground">Henüz hesap eklenmedi.</p>}
        {items.map((a) => {
          const Meta = TYPE_META[a.type] || TYPE_META.bank;
          return (
            <div key={a.id} data-testid={`account-card-${a.id}`} className="bg-card border border-border rounded-lg p-6 transition-transform hover:-translate-y-1">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                  <Meta.icon size={20} className="text-primary" />
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openMovements(a)} data-testid={`account-movements-${a.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1" title="Hareketler">
                    <ListDashes size={16} />
                  </button>
                  <button onClick={() => openEdit(a)} data-testid={`account-edit-${a.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1">
                    <PencilSimple size={16} />
                  </button>
                  <ConfirmDelete onConfirm={() => remove(a.id)} testId={`account-delete-${a.id}`}
                    title="Hesabı sil?" description={`"${a.name}" hesabı silinecek. Bu işlem geri alınamaz.`}>
                    <Trash size={16} />
                  </ConfirmDelete>
                </div>
              </div>
              <div className="mt-4 text-sm font-medium">{a.name}</div>
              <div className="text-xs text-muted-foreground">{a.bank_name || Meta.label}</div>
              <div className="mt-3 font-heading text-2xl font-semibold font-mono-num">{formatMoney(a.balance, a.currency, currencies)}</div>
              <div className="text-[10px] uppercase tracking-wide text-primary mt-1">{a.currency} · {Meta.label}</div>
            </div>
          );
        })}
      </div>

      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Hesap Hareketleri</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {movements.account ? `${movements.account.name} · ${movements.account.currency}` : ""}
            </DialogDescription>
          </DialogHeader>
          {movements.account && (
            <div className="flex items-center justify-between border border-border rounded-md px-4 py-3 bg-secondary/30">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Güncel Bakiye</span>
              <span className="font-heading text-xl font-semibold font-mono-num">{formatMoney(movements.account.balance, movements.account.currency, currencies)}</span>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="col-span-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Başlangıç</Label>
              <Input type="date" value={moveFilter.start} onChange={(e) => applyFilter({ start: e.target.value })} className="mt-1 h-9 bg-secondary border-border text-xs" data-testid="move-filter-start" />
            </div>
            <div className="col-span-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Bitiş</Label>
              <Input type="date" value={moveFilter.end} onChange={(e) => applyFilter({ end: e.target.value })} className="mt-1 h-9 bg-secondary border-border text-xs" data-testid="move-filter-end" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Tür</Label>
              <Select value={moveFilter.type} onValueChange={(v) => applyFilter({ type: v })}>
                <SelectTrigger className="mt-1 h-9 bg-secondary border-border text-xs" data-testid="move-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="income">Gelir</SelectItem>
                  <SelectItem value="expense">Gider</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 sm:col-span-1 flex items-end gap-1">
              <Button onClick={() => exportStatement("xlsx")} data-testid="statement-xlsx" variant="outline" size="sm" className="flex-1 border-border hover:bg-secondary h-9 px-2" title="Excel">
                <FileXls size={16} />
              </Button>
              <Button onClick={() => exportStatement("pdf")} data-testid="statement-pdf" variant="outline" size="sm" className="flex-1 border-border hover:bg-secondary h-9 px-2" title="PDF">
                <FilePdf size={16} />
              </Button>
            </div>
          </div>
          <div className="max-h-[45vh] overflow-y-auto space-y-2 mt-2" data-testid="account-movements-list">
            {movements.transactions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Bu hesaba ait hareket yok.</p>
            ) : movements.transactions.map((t) => (
              <div key={t.id} data-testid={`movement-${t.id}`} className="flex items-center gap-3 border border-border rounded-md p-3 bg-secondary/20">
                <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${t.type === "income" ? "bg-success/10" : "bg-destructive/10"}`}>
                  {t.type === "income" ? <ArrowDownLeft size={16} className="text-[#10B981]" /> : <ArrowUpRight size={16} className="text-[#EF4444]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.category || (t.type === "income" ? "Gelir" : "Gider")}</div>
                  <div className="text-xs text-muted-foreground truncate">{t.description || "-"} · {formatDate(t.date)}</div>
                </div>
                <div className={`font-mono-num font-semibold text-sm text-right shrink-0 ${t.type === "income" ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                  {t.type === "income" ? "+" : "−"}{formatMoney(t.amount, t.currency, currencies)}
                </div>
                {isEditable(t) && (
                  <div className="flex gap-0.5 shrink-0">
                    <button onClick={() => openTxEdit(t)} data-testid={`movement-edit-${t.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1" title="Düzenle">
                      <PencilSimple size={15} />
                    </button>
                    <ConfirmDelete onConfirm={() => removeTx(t.id)} testId={`movement-delete-${t.id}`}
                      title="Hareketi sil?" description="Bu gelir/gider kaydı silinecek ve hesap bakiyesi güncellenecek. Bu işlem geri alınamaz.">
                      <Trash size={15} />
                    </ConfirmDelete>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!txEdit} onOpenChange={(o) => !o && setTxEdit(null)}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Hareketi Düzenle</DialogTitle>
            <DialogDescription className="text-muted-foreground">Tutar ve açıklamayı güncelleyin. Hesap bakiyesi otomatik düzeltilir.</DialogDescription>
          </DialogHeader>
          {txEdit && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tür</Label>
                  <Select value={txEdit.type} onValueChange={setTxType}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="movement-edit-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">Gelir</SelectItem>
                      <SelectItem value="expense">Gider</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Kategori</Label>
                  <Select value={txEdit.category} onValueChange={(v) => setTxEdit((f) => ({ ...f, category: v }))}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="movement-edit-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {catsFor(txEdit.type, txEdit.category).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Tutar</Label>
                <Input type="number" value={txEdit.amount} onChange={(e) => setTxEdit((f) => ({ ...f, amount: e.target.value }))} className="mt-1.5 bg-secondary border-border" data-testid="movement-edit-amount" placeholder="0" />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Açıklama</Label>
                <Input value={txEdit.description} onChange={(e) => setTxEdit((f) => ({ ...f, description: e.target.value }))} className="mt-1.5 bg-secondary border-border" data-testid="movement-edit-description" />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Tarih</Label>
                <Input type="date" value={txEdit.date} onChange={(e) => setTxEdit((f) => ({ ...f, date: e.target.value }))} className="mt-1.5 bg-secondary border-border" data-testid="movement-edit-date" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveTxEdit} data-testid="movement-edit-save" className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Transfer Geçmişi</DialogTitle>
            <DialogDescription className="text-muted-foreground">Hesaplar arası yapılan tüm transferler.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto space-y-2" data-testid="transfer-history-list">
            {transfers.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Henüz transfer yapılmadı.</p>
            ) : transfers.map((t) => (
              <div key={t.group} data-testid={`transfer-${t.group}`} className="border border-border rounded-md p-3 bg-secondary/20">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium truncate">{t.from_name}</span>
                  <ArrowsLeftRight size={14} className="text-primary shrink-0" />
                  <span className="font-medium truncate">{t.to_name}</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{formatDate(t.date)}{t.note ? ` · ${t.note}` : ""}</span>
                  <span className="font-mono-num text-sm font-semibold">
                    {formatMoney(t.from_amount, t.from_currency, currencies)}
                    {t.from_currency !== t.to_currency && <span className="text-muted-foreground"> → {formatMoney(t.to_amount, t.to_currency, currencies)}</span>}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={xferOpen} onOpenChange={setXferOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">Hesaplar Arası Transfer</DialogTitle>
            <DialogDescription className="text-muted-foreground">Bir hesaptan diğerine para aktarın; her iki hesabın hareketine işlenir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Kaynak Hesap</Label>
              <Select value={xfer.from_account_id} onValueChange={(v) => setX("from_account_id", v)}>
                <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="xfer-from"><SelectValue placeholder="Hesap seçin" /></SelectTrigger>
                <SelectContent>
                  {items.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {formatMoney(a.balance, a.currency, currencies)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Hedef Hesap</Label>
              <Select value={xfer.to_account_id} onValueChange={(v) => setX("to_account_id", v)}>
                <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="xfer-to"><SelectValue placeholder="Hesap seçin" /></SelectTrigger>
                <SelectContent>
                  {items.filter((a) => a.id !== xfer.from_account_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className={diffCurrency ? "grid grid-cols-2 gap-3" : ""}>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Tutar {fromAcc ? `(${fromAcc.currency})` : ""}</Label>
                <Input type="number" value={xfer.amount} onChange={(e) => setX("amount", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="xfer-amount" placeholder="0" />
              </div>
              {diffCurrency && (
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Hedef Tutar ({toAcc.currency})</Label>
                  <Input type="number" value={xfer.to_amount} onChange={(e) => setX("to_amount", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="xfer-to-amount" placeholder="0" />
                </div>
              )}
            </div>
            {diffCurrency && <p className="text-[11px] text-amber-500/90">Para birimleri farklı — hedef hesaba geçecek tutarı ayrıca girin.</p>}
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Açıklama (opsiyonel)</Label>
              <Input value={xfer.description} onChange={(e) => setX("description", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="xfer-desc" placeholder="Örn. Kasa aktarımı" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitXfer} data-testid="xfer-save" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <ArrowsLeftRight size={16} className="mr-1.5" /> Transfer Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
